import {Plural, Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useState} from 'react';
import {connect} from 'react-redux';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {makeMapDispatchToProps} from '../../helpers/reselectHelpers';
import {AnswerFolderModel, AnswerOrFolderModel, RAnswerFolder} from '../../models/api/answerFolderModel';
import {AnswerModel} from '../../models/api/answerModel';
import {SettingsAnswersUrl} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {deleteAnswersOrFolderBulkAsync} from '../../store/data/answers/answerBulkActions';
import {getSettingsType} from '../rules/ruleHelpers';

/*
 * Props.
 */

interface DeleteAnswerDialogContainerProps {
  itemsToActOn: ReadonlyArray<AnswerOrFolderModel>;
  answersUrl: SettingsAnswersUrl;
  onRequestClose: Handler;
  onDelete: Handler;
}

/*
 * Component.
 */

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  deleteAnswersOrFolderBulk: deleteAnswersOrFolderBulkAsync,
}));

interface DeleteAnswerDialogContainerComponentProps
  extends DeleteAnswerDialogContainerProps,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

const DeleteAnswerDialogContainerComponent: FC<DeleteAnswerDialogContainerComponentProps> = (props) => {
  const {itemsToActOn, answersUrl} = props;
  const [isDeleting, setIsDeleting] = useState(false);

  const [foldersToActOn, answersToActOn] = _(itemsToActOn)
    .partition((i) => RAnswerFolder.is(i))
    .value() as [AnswerFolderModel[], AnswerModel[]];

  const renderTotalItemsLabel = () => {
    const folderPlural = (
      <Plural
        id="settings-answersdeletedialog-selectedfolders"
        value={foldersToActOn.length}
        zero="No folders"
        one="1 folder"
        other="# folders"
      />
    );

    const answerPlural = (
      <Plural
        id="settings-answersdeletedialog-selectedanswers"
        value={answersToActOn.length}
        zero="No templates"
        one="1 template"
        other="# templates"
      />
    );

    if (foldersToActOn.length > 0 && answersToActOn.length === 0) {
      return folderPlural;
    }

    if (foldersToActOn.length === 0 && answersToActOn.length > 0) {
      return answerPlural;
    }

    return (
      <Trans id="settings-answers-selecteditems">
        {folderPlural} and {answerPlural}
      </Trans>
    );
  };

  const title = <Trans id="settings-answers-delete-title">Delete {renderTotalItemsLabel()}</Trans>;

  const renderDeleteBodyMessage = () => {
    const answerPlural = (
      <Plural
        id="delete-answers-confirmation-dialog-description"
        value={answersToActOn.length}
        one="This template will be permanently deleted in Front. This action cannot be undone."
        other="These templates will be permanently deleted in Front. This action cannot be undone."
      />
    );

    const folderMessage = (
      <Trans id="answerdialogdelete-body">
        This will permanently delete any message templates, folders, sub-folders and the contents of any
        sub-folders.
      </Trans>
    );

    if (foldersToActOn.length === 0 && answersToActOn.length > 0) {
      return answerPlural;
    }

    return folderMessage;
  };

  const onOkDeleteClick = async () => {
    try {
      setIsDeleting(true);
      await props.deleteAnswersOrFolderBulk(
        answersUrl.namespace,
        foldersToActOn.map((i) => i.id),
        answersToActOn.map((i) => i.id),
      );

      props.onDelete();
      answersToActOn.forEach((answer) => {
        trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_CANNED_ANSWER_DELETE, {
          id: answer.id,
          canned_answer_folder_id: answer.folderId,
          if_nested_folder: Boolean(answer),
          settings_type: getSettingsType(answersUrl.namespace),
        });
      });
      if (foldersToActOn.length > 0) {
        trackInterfaceEvent(InterfaceEventsEnum.CANNED_ANSWER_FOLDER_DELETE, {
          canned_answer_folder_ids: foldersToActOn.map((i) => i.id),
          has_sub_folders: foldersToActOn.some((f) => f.numChildFolders > 0),
          has_canned_responses: foldersToActOn.some((f) => f.numChildAnswers > 0),
          settings_type: getSettingsType(answersUrl.namespace),
        });
      }
    } catch (error) {
      setIsDeleting(false);
      throw error;
    }
  };

  return (
    <OkCancelDialog
      title={title}
      okButtonLabel={<Trans id="answerdialogdelete-delete">Delete</Trans>}
      okButtonVariant={ButtonVariantsEnum.DANGER}
      onRequestClose={props.onRequestClose}
      onOkClick={onOkDeleteClick}
      isOkDisabled={isDeleting}
    >
      {renderDeleteBodyMessage()}
    </OkCancelDialog>
  );
};

export const DeleteAnswerDialogContainer = connect(
  null,
  mapDispatchToProps,
)(DeleteAnswerDialogContainerComponent);
